import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Star, Users, ArrowLeft, ArrowRight, FileText, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mockData, iconMap } from '../mock';

const ServiceCategoryPage = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const category = mockData.serviceCategories.find(c => c.slug === categorySlug);
  const details = category ? category.details : null;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (details && details.seo) {
        document.title = details.seo.title;
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.name = 'description';
          document.head.appendChild(metaDesc);
        }
        metaDesc.content = details.seo.description;
      } else if (category) {
        document.title = `${category.title} | APK Fintech`;
      }
    }, 100);
    return () => {
      clearTimeout(timer);
      document.title = 'APKfintech';
    };
  }, [categorySlug, details, category]);

  if (!category) {
    return (
      <div className="getstarted-not-found">
        <h2>Category not found</h2>
        <Link to="/services"><Button className="btn-primary"><ArrowLeft size={18} /> All Services</Button></Link>
      </div>
    );
  }

  return (
    <div className="category-page" data-testid="category-page">
      <div className="category-page-container">

        {/* Breadcrumb */}
        <div className="getstarted-breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <span className="breadcrumb-sep">/</span>
          <Link to="/services" className="breadcrumb-link">Services</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{category.title}</span>
        </div>

        <h1 className="category-page-title" data-testid="category-page-title">{category.title}</h1>

        {/* Hero Section */}
        {details && details.hero && (
          <div className="category-hero" data-testid="category-hero">
            <h2 className="category-hero-headline">{details.hero.headline}</h2>
            <div className="category-hero-subheadline">
              {details.hero.subheadline.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
            {details.hero.tagline && (
              <p className="category-hero-tagline-italic">{details.hero.tagline}</p>
            )}
            {details.hero.buttonLink ? (
              <a href={details.hero.buttonLink}>
                <Button className="btn-primary category-hero-cta" data-testid="hero-cta-btn">
                  {details.hero.buttonText} <ArrowRight size={20} />
                </Button>
              </a>
            ) : (
              <Link to="/contact">
                <Button className="btn-primary category-hero-cta" data-testid="hero-cta-btn">
                  {details.hero.buttonText} <ArrowRight size={20} />
                </Button>
              </Link>
            )}

            {details.hero.stats && (
              <div className="category-hero-stats" data-testid="hero-stats">
                {details.hero.stats.map((stat, i) => (
                  <div key={i} className="category-hero-stat">
                    <span className="hero-stat-value">{stat.value}</span>
                    <span className="hero-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            )}

            {details.hero.disclaimer && (
              <p className="category-hero-disclaimer">{details.hero.disclaimer}</p>
            )}
          </div>
        )}

        {/* Taxation Services Intro */}
        {details && details.taxationServices && (
          <div className="tax-intro-section" data-testid="taxation-services-intro">
            <span className="about-mv-label">{details.taxationServices.label}</span>
            <h2 className="about-section-heading">{details.taxationServices.heading}</h2>
            {details.taxationServices.paragraphs.map((para, idx) => (
              <p key={idx} className="tax-intro-para">{para}</p>
            ))}
          </div>
        )}

        {/* Tax Service Cards */}
        {details && details.taxServiceCards && (
          <div className="tax-cards-section" data-testid="tax-service-cards">
            {details.taxServiceCards.map((card, cIdx) => (
              <div key={cIdx} className="tax-card" data-testid={`tax-card-${cIdx}`}>
                <h3 className="tax-card-title">{card.title}</h3>
                <p className="tax-card-intro">{card.intro}</p>
                {card.sections.map((sec, sIdx) => (
                  <div key={sIdx} className="tax-card-block">
                    <h4 className="tax-card-block-heading">{sec.heading}</h4>
                    <ul className="tax-card-checklist">
                      {sec.items.map((item, iIdx) => (
                        <li key={iIdx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Why APK Section */}
        {details && details.whyApk && (
          <div className="tax-why-section" data-testid="tax-why-apk">
            <span className="about-mv-label">{details.whyApk.label}</span>
            <h2 className="about-section-heading">{details.whyApk.heading}</h2>
            <div className="tax-why-grid">
              {details.whyApk.tiles.map((tile, idx) => {
                const IconComp = iconMap[tile.icon] || Star;
                return (
                  <div key={idx} className="about-voice-tile" data-testid={`why-tile-${idx}`}>
                    <div className="about-voice-icon"><IconComp size={24} /></div>
                    <h4 className="about-voice-title">{tile.title}</h4>
                    <p className="about-voice-body">{tile.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Structure Guide Section */}
        {details && details.structureGuide && (
          <div className="structure-guide-section" data-testid="structure-guide">
            <span className="about-mv-label">{details.structureGuide.label}</span>
            <h2 className="about-section-heading">{details.structureGuide.heading}</h2>
            <p className="tax-intro-para">{details.structureGuide.intro}</p>
            <div className="structure-cards">
              {details.structureGuide.structures.map((struct, idx) => (
                <div key={idx} className="structure-card" data-testid={`structure-${idx}`}>
                  <h3 className="structure-card-title">{struct.title}</h3>
                  <div className="structure-best-for">
                    <span className="structure-best-label">Best For:</span> {struct.bestFor}
                  </div>
                  <h4 className="structure-features-heading">Key Features</h4>
                  <ul className="tax-card-checklist">
                    {struct.features.map((f, fIdx) => (
                      <li key={fIdx}>{f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Also Available Section */}
        {details && details.alsoAvailable && (
          <div className="tax-why-section" data-testid="also-available">
            <span className="about-mv-label">{details.alsoAvailable.label}</span>
            <h2 className="about-section-heading">{details.alsoAvailable.heading}</h2>
            <div className="inh-serve-grid">
              {details.alsoAvailable.tiles.map((tile, idx) => (
                <div key={idx} className="inh-serve-card" data-testid={`also-tile-${idx}`}>
                  <h4 className="inh-serve-card-title">{tile.title}</h4>
                  <p className="inh-serve-card-body">{tile.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NRI Services Section */}
        {details && details.nriServices && (
          <div className="tax-cards-section" data-testid="nri-services">
            <span className="about-mv-label">{details.nriServices.label}</span>
            <h2 className="about-section-heading">{details.nriServices.heading}</h2>
            <p className="tax-intro-para" style={{ marginBottom: '28px' }}>{details.nriServices.intro}</p>
            <div className="tax-card" data-testid="nri-considerations">
              <h3 className="tax-card-title">Key Considerations for NRI Business Formation</h3>
              {details.nriServices.categories.map((cat, idx) => (
                <div key={idx} className="tax-card-block">
                  <h4 className="tax-card-block-heading">{cat.title}</h4>
                  <ul className="tax-card-checklist">
                    {cat.items.map((item, iIdx) => (
                      <li key={iIdx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Succession Laws Section */}
        {details && details.successionLaws && (
          <div className="succession-section" data-testid="succession-laws">
            <h2 className="succession-heading">{details.successionLaws.heading}</h2>
            <p className="succession-intro">{details.successionLaws.intro}</p>
            <div className="succession-grid">
              {details.successionLaws.cards.map((card, i) => (
                <div key={i} className="succession-card" data-testid={`succession-card-${i}`}>
                  <h4 className="succession-card-title">{card.title}</h4>
                  <p className="succession-card-body">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nominee Myth Section */}
        {details && details.nomineeMyth && (
          <div className="nominee-section" data-testid="nominee-myth">
            <h2 className="nominee-heading">{details.nomineeMyth.heading}</h2>
            <div className="nominee-boxes">
              <div className="nominee-box nominee-box-incorrect">
                <span className="nominee-box-label">{details.nomineeMyth.incorrect.label}</span>
                <blockquote className="nominee-box-quote">{details.nomineeMyth.incorrect.quote}</blockquote>
                <p className="nominee-box-body">{details.nomineeMyth.incorrect.body}</p>
              </div>
              <div className="nominee-box nominee-box-correct">
                <span className="nominee-box-label">{details.nomineeMyth.correct.label}</span>
                <blockquote className="nominee-box-quote">{details.nomineeMyth.correct.quote}</blockquote>
                <p className="nominee-box-body">{details.nomineeMyth.correct.body}</p>
              </div>
            </div>
            <div className="nominee-cta">
              <Link to="/contact">
                <Button className="btn-primary" data-testid="nominee-cta-btn">
                  {details.nomineeMyth.buttonText} <ArrowRight size={20} />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Inheritance Planning Services Section */}
        {details && details.inheritanceServices && (
          <div className="inh-services-section" data-testid="inheritance-services">
            <h2 className="inh-services-heading">{details.inheritanceServices.heading}</h2>
            {details.inheritanceServices.items.map((item, idx) => (
              <div key={idx} className="inh-service-panel" data-testid={`inh-service-${idx}`}>
                <div className="inh-service-header">
                  <h3 className="inh-service-title">{item.title}</h3>
                  <p className="inh-service-tagline">{item.tagline}</p>
                </div>
                <div className="inh-service-body">
                  {item.description.split('\n\n').map((para, pIdx) => (
                    <p key={pIdx} className="inh-service-desc">{para}</p>
                  ))}

                  {item.featureColumns && (
                    <div className="inh-feature-columns" data-testid={`inh-features-${idx}`}>
                      {item.featureColumns.map((col, cIdx) => (
                        <div key={cIdx} className="inh-feature-col">
                          <h4 className="inh-feature-col-title">{col.title}</h4>
                          <p className="inh-feature-col-body">{col.body}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.importantUpdate && (
                    <div className="inh-important-update" data-testid={`inh-update-${idx}`}>
                      <span className="inh-update-label">{item.importantUpdate.label}</span>
                      <p className="inh-update-text">{item.importantUpdate.text}</p>
                    </div>
                  )}

                  {item.bullets && (
                    <>
                      <h4 className="inh-service-bullets-heading">{item.bulletsHeading || 'What We Cover'}</h4>
                      <ul className="inh-service-bullets">
                        {item.bullets.map((bullet, bIdx) => (
                          <li key={bIdx}>{bullet}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {item.callout && (
                    <div className="inh-service-callout" data-testid={`inh-callout-${idx}`}>
                      <div className="inh-callout-icon">
                        <FileText size={20} />
                      </div>
                      <div className="inh-callout-content">
                        <span className="inh-callout-label">{item.callout.label || 'Important Note'}</span>
                        <p>{item.callout.text}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Process Steps Timeline */}
        {details && details.processSteps && (
          <div className="inh-process-section" data-testid="process-steps">
            <h2 className="inh-process-heading">{details.processSteps.heading}</h2>
            <div className="inh-process-timeline">
              {details.processSteps.steps.map((step, idx) => (
                <div key={idx} className="inh-process-step" data-testid={`process-step-${idx}`}>
                  <div className="inh-step-marker">
                    <span className="inh-step-number">{idx + 1}</span>
                    {idx < details.processSteps.steps.length - 1 && <div className="inh-step-line" />}
                  </div>
                  <div className="inh-step-content">
                    <h4 className="inh-step-title">Step {idx + 1} — {step.title}</h4>
                    <p className="inh-step-body">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Who We Serve Section */}
        {details && details.whoWeServe && (
          <div className="inh-serve-section" data-testid="who-we-serve">
            <h2 className="inh-serve-heading">{details.whoWeServe.heading}</h2>
            <div className="inh-serve-grid">
              {details.whoWeServe.cards.map((card, idx) => (
                <div key={idx} className="inh-serve-card" data-testid={`serve-card-${idx}`}>
                  <h4 className="inh-serve-card-title">{card.title}</h4>
                  <p className="inh-serve-card-body">{card.body}</p>
                  <p className="inh-serve-card-services"><span>Services for you:</span> {card.services}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Advantage Section */}
        {details && details.advantage && (
          <div className="inh-advantage-section" data-testid="advantage-section">
            <h2 className="inh-advantage-heading">{details.advantage.heading}</h2>
            <p className="inh-advantage-intro">{details.advantage.intro}</p>
            <div className="inh-advantage-list">
              {details.advantage.items.map((item, idx) => (
                <div key={idx} className="inh-advantage-block" data-testid={`advantage-${idx}`}>
                  <span className="inh-advantage-number">{item.number}</span>
                  <div className="inh-advantage-content">
                    <h4 className="inh-advantage-title">{item.title}</h4>
                    <p className="inh-advantage-body">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="inh-advantage-cta">
              <Link to="/contact">
                <Button className="btn-primary" data-testid="advantage-cta-btn">
                  {details.advantage.ctaText} <ArrowRight size={20} />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Simple Details (Mutual Funds style) */}
        {details && details.tagline && !details.sections && (
          <div className="category-details" data-testid="category-details">
            <p className="category-tagline">{details.tagline}</p>
            <p className="category-description">{details.description}</p>

            <h3 className="category-highlights-heading">{details.heading}</h3>
            <ul className="category-highlights">
              {details.highlights.map((item, idx) => (
                <li key={idx} className="category-highlight-item">{item}</li>
              ))}
            </ul>

            {details.subServices && (
              <>
                <h4 className="category-subservices-heading">Sub-Services</h4>
                <div className="category-subservices">
                  {details.subServices.map((sub, idx) => (
                    <span key={idx} className="category-subservice-tag">{sub}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Multi-Section Details (PMS & AIFs style) */}
        {details && details.sections && (
          <div className="category-details-multi" data-testid="category-details-multi">
            {details.sections.map((section, sIdx) => (
              <div key={sIdx} className="detail-section">
                <h2 className="detail-section-title">{section.title}</h2>
                <p className="category-tagline">{section.tagline}</p>
                <p className="category-description">{section.description}</p>

                {section.minimumInvestment && (
                  <div className="detail-min-investment">
                    <span className="min-invest-label">SEBI Minimum Investment:</span>
                    <span className="min-invest-value">{section.minimumInvestment}</span>
                  </div>
                )}

                {section.pmsTypes && (
                  <div className="detail-types-grid">
                    {section.pmsTypes.map((t, i) => (
                      <div key={i} className="detail-type-card">
                        <h4 className="detail-type-name">{t.name}</h4>
                        <p className="detail-type-desc">{t.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {section.aifCategories && (
                  <div className="detail-types-grid">
                    {section.aifCategories.map((cat, i) => (
                      <div key={i} className="detail-type-card">
                        <h4 className="detail-type-name">{cat.name}</h4>
                        <p className="detail-type-focus">{cat.focus}</p>
                        <ul className="detail-type-items">
                          {cat.items.map((item, j) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {section.stats && (
                  <div className="detail-stats-bar">
                    {section.stats.map((stat, i) => (
                      <div key={i} className="detail-stat">
                        <span className="detail-stat-value">{stat.value}</span>
                        <span className="detail-stat-label">{stat.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <h3 className="category-highlights-heading">{section.heading}</h3>
                <ul className="category-highlights">
                  {section.highlights.map((item, idx) => (
                    <li key={idx} className="category-highlight-item">{item}</li>
                  ))}
                </ul>

                {section.subServices && (
                  <>
                    <h4 className="category-subservices-heading">Sub-Services</h4>
                    <div className="category-subservices">
                      {section.subServices.map((sub, idx) => (
                        <span key={idx} className="category-subservice-tag">{sub}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}

            {details.comparisonTable && (
              <div className="detail-comparison">
                <h3 className="comparison-title">PMS vs AIF — Quick Comparison</h3>
                <div className="comparison-table-wrapper">
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        {details.comparisonTable.headers.map((h, i) => (
                          <th key={i}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {details.comparisonTable.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {details.riskDisclosure && (
              <div className="detail-risk-disclosure">
                <p>{details.riskDisclosure}</p>
              </div>
            )}
          </div>
        )}

        {/* No details yet placeholder */}
        {!details && (
          <div className="category-no-details">
            <p>Detailed content coming soon. Explore our services below.</p>
          </div>
        )}

        {/* Service Cards */}
        <div className="category-page-cards">
          <h3 className="category-cards-heading">Get Started</h3>
          <div className="catalog-cards-grid">
            {category.services.map((service, sIdx) => {
              const IconComponent = iconMap[service.icon] || Star;
              return (
                <div
                  key={sIdx}
                  className="catalog-card"
                  data-testid={`service-card-${service.slug}`}
                  onClick={() => navigate(`/contact?service=${encodeURIComponent(service.name)}`)}
                >
                  <div className="catalog-card-icon">
                    <IconComponent size={28} />
                  </div>
                  <p className="catalog-card-name">{service.name}</p>
                  <div className="catalog-card-meta">
                    <span className="catalog-card-rating">
                      {service.rating} <Star size={12} fill="#00A86B" stroke="#00A86B" />
                    </span>
                    <span className="catalog-card-users">
                      {service.users} <Users size={12} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQ Accordion */}
        {details && details.faq && (
          <div className="inh-faq-section" data-testid="faq-section">
            <h2 className="inh-faq-heading">{details.faq.heading}</h2>
            <div className="inh-faq-list">
              {details.faq.items.map((item, idx) => (
                <div
                  key={idx}
                  className={`inh-faq-item ${openFaq === idx ? 'inh-faq-open' : ''}`}
                  data-testid={`faq-item-${idx}`}
                >
                  <button
                    className="inh-faq-question"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    data-testid={`faq-toggle-${idx}`}
                  >
                    <span>{item.q}</span>
                    <ChevronDown size={20} className="inh-faq-chevron" />
                  </button>
                  {openFaq === idx && (
                    <div className="inh-faq-answer" data-testid={`faq-answer-${idx}`}>
                      <p>{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legal Disclaimer */}
        {details && details.disclaimer && (
          <div className="inh-disclaimer" data-testid="page-disclaimer">
            <p>{details.disclaimer}</p>
          </div>
        )}

      </div>

      {/* Final CTA (Full Width) */}
      {details && details.finalCta && (
        <div className="about-final-cta" data-testid="page-final-cta">
          <div className="about-final-cta-inner">
            <span className="about-final-label">{details.finalCta.label}</span>
            <h2 className="about-final-headline">{details.finalCta.headline}</h2>
            <p className="about-final-tagline">{details.finalCta.tagline}</p>
            <p className="cat-final-supporting">{details.finalCta.supportingText}</p>
            <div className="mf-final-cta-buttons">
              <a href={details.finalCta.primaryButton.link}>
                <Button className="btn-primary mf-final-btn-primary" data-testid="final-cta-primary">
                  {details.finalCta.primaryButton.text} <ArrowRight size={20} />
                </Button>
              </a>
            </div>
            {details.finalCta.website && (
              <p className="about-final-website">{details.finalCta.website}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCategoryPage;
